import {Autocomplete, Checkbox, FormControlLabel, FormGroup, Stack, Switch} from "@mui/material";
import TextField from "@mui/material/TextField";
import {useTranslation} from "react-i18next";
import Property from "crqlar-react-common/dist/components/Property";
import AdditionalProperties from "crqlar-react-common/dist/components/AdditionalProperties";
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import type {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import {AcademyProfileTags, academyDepartmentOptions, withAcademyFlag} from "./academyProfile.util";
import {
    ACADEMY_LEVELS,
    academyTagFlag,
    academyTagList,
    academyTagText,
    withAcademyList,
    withAcademyTag,
} from "../../common/academyTags.util";

interface Props {
    profile: Profile;
    /** The organisation's Akademie sector: the reason this section exists, and its department list. */
    sector: Sector;
    onTagsChange: (tags: Record<string, unknown>) => void;
}

/**
 * The Forsthofgut Akademie's settings for one person, as labelled controls instead of raw tags.
 *
 * Editable even on a profile the PMS manages, which is the point of it: these values are tags, the
 * PMS sync leaves tags alone, so this is the one part of such a profile the hotel can still set.
 */
const AcademySection = ({profile, sector, onTagsChange}: Props) => {
    const {t} = useTranslation()
    const tags = profile.tags as Record<string, unknown> | undefined

    const isEmployee = academyTagFlag(tags, AcademyProfileTags.EMPLOYEE)
    const levels = academyTagList(tags, AcademyProfileTags.LEVEL)
    const department = academyTagText(tags, AcademyProfileTags.DEPARTMENT) ?? null
    const approvesFor = academyTagList(tags, AcademyProfileTags.APPROVES_FOR)
    const departments = academyDepartmentOptions(sector, tags)
    // Without a configured list there is nothing to choose from, so typing is the only way in.
    const freeText = departments.length === 0
    const profileDepartment = profile.department?.trim()

    const toggleLevel = (level: string, on: boolean) => onTagsChange(withAcademyList(
        tags,
        AcademyProfileTags.LEVEL,
        ACADEMY_LEVELS.filter(l => l === level ? on : levels.includes(l)),
    ))

    return (
        <AdditionalProperties
            title={t('Akademie')}
            subtitle={t('Stored on the profile itself, so a PMS sync does not overwrite it.')}
            variant={'outlined'}
        >
            <Stack gap={{xs: 1.5, sm: 1}}>
                <Property
                    title={t('Team member')}
                    subtitle={t('Opens the TEAM area of the app and the Akademie for this person.')}
                >
                    <Switch
                        id={'academy-employee'}
                        checked={isEmployee}
                        onChange={(e) => onTagsChange(withAcademyFlag(tags, AcademyProfileTags.EMPLOYEE, e.target.checked))}
                    />
                </Property>

                {isEmployee && (
                    <>
                        <Property
                            title={t('Akademie department')}
                            subtitle={profileDepartment
                                ? t('Leave empty to use the profile department ({{department}}).', {department: profileDepartment})
                                : t('Requests from this person go to the heads of this department.')}
                        >
                            <Autocomplete
                                id={'academy-department'}
                                size={'small'}
                                fullWidth
                                freeSolo={freeText}
                                options={departments}
                                value={department}
                                onChange={(_, value) => onTagsChange(
                                    withAcademyTag(tags, AcademyProfileTags.DEPARTMENT, value ?? undefined)
                                )}
                                onInputChange={freeText
                                    ? (_, value, reason) => reason === 'input' && onTagsChange(
                                        withAcademyTag(tags, AcademyProfileTags.DEPARTMENT, value)
                                    )
                                    : undefined}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder={profileDepartment || t('Select')}/>
                                )}
                            />
                        </Property>

                        <Property
                            title={t('Levels')}
                            subtitle={t('Which course levels this person may book.')}
                        >
                            <FormGroup row>
                                {ACADEMY_LEVELS.map(level => (
                                    <FormControlLabel
                                        key={level}
                                        label={t('Level {{level}}', {level})}
                                        control={
                                            <Checkbox
                                                id={`academy-level-${level}`}
                                                checked={levels.includes(level)}
                                                onChange={(e) => toggleLevel(level, e.target.checked)}
                                            />
                                        }
                                    />
                                ))}
                            </FormGroup>
                        </Property>

                        <Property
                            title={t('Decides requests for')}
                            subtitle={t('Departments whose course requests and attendance this person confirms. Empty for most employees.')}
                        >
                            <Autocomplete
                                id={'academy-approves-for'}
                                size={'small'}
                                fullWidth
                                multiple
                                freeSolo={freeText}
                                options={departments}
                                value={approvesFor}
                                onChange={(_, value) => onTagsChange(
                                    withAcademyList(tags, AcademyProfileTags.APPROVES_FOR, value as string[])
                                )}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder={approvesFor.length ? undefined : t('None')}/>
                                )}
                            />
                        </Property>
                    </>
                )}
            </Stack>
        </AdditionalProperties>
    )
}

export default AcademySection
